import React, { useMemo, useState } from 'react';

import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import actionLibraryInstancesApi from 'config/api/actionLibraryInstance/actionLibraryInstance';
import type { InstanceComment, InstanceCommentPurpose } from 'config/api/actionLibraryInstance/actionLibraryInstance.types';
import action_library_messages from 'messages/action_library_messages';
import general_messages from 'messages/general_messages';

const PURPOSE_DELEGATION: InstanceCommentPurpose = 2;
const PURPOSE_COMPLETION: InstanceCommentPurpose = 3;

const getPurposeMessageKey = (purpose: InstanceCommentPurpose | undefined) => {
  if (purpose === PURPOSE_DELEGATION) return action_library_messages.instance_actions.comment_purpose_delegation;
  if (purpose === PURPOSE_COMPLETION) return action_library_messages.instance_actions.comment_purpose_completion;
  return action_library_messages.instance_actions.comment_purpose_general;
};

const INSTANCE_COMMENTS_QUERY_KEY = 'ACTION_LIBRARY_INSTANCE_COMMENTS';

type InstanceCommentsDialogProps = {
  instanceId: number;
  onClose: () => void;
  onCommentAdded?: () => void;
  open: boolean;
  readOnly?: boolean;
};

type SortOrder = 'desc' | 'asc';

const InstanceCommentsDialog: React.FC<InstanceCommentsDialogProps> = ({ open, onClose, instanceId, onCommentAdded, readOnly }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [newCommentText, setNewCommentText] = useState('');
  const [dateSortOrder, setDateSortOrder] = useState<SortOrder>('desc');

  const { data: commentsData, isLoading } = useQuery(
    [INSTANCE_COMMENTS_QUERY_KEY, instanceId],
    () => actionLibraryInstancesApi.getInstanceComments(instanceId).then(res => res.data),
    { enabled: open && !!instanceId },
  );

  const createCommentMutation = useMutation((text: string) => actionLibraryInstancesApi.createInstanceComment(instanceId, { text }), {
    onSuccess: () => {
      queryClient.invalidateQueries([INSTANCE_COMMENTS_QUERY_KEY, instanceId]);
      setNewCommentText('');
      onCommentAdded?.();
    },
  });

  const rawComments = commentsData?.results ?? [];
  const sortedComments = useMemo(
    () =>
      [...rawComments].sort((a, b) => {
        const timeA = new Date(a.createdAt).getTime();
        const timeB = new Date(b.createdAt).getTime();
        return dateSortOrder === 'desc' ? timeB - timeA : timeA - timeB;
      }),
    [rawComments, dateSortOrder],
  );

  const handleDateSortClick = () => setDateSortOrder(prev => (prev === 'desc' ? 'asc' : 'desc'));

  const formatDate = (dateStr: string) => {
    try {
      return format(new Date(dateStr), 'yyyy-MM-dd HH:mm');
    } catch {
      return dateStr;
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = newCommentText.trim();
    if (!trimmed || createCommentMutation.isLoading) return;
    createCommentMutation.mutate(trimmed);
  };

  const renderCommentsContent = () => {
    if (isLoading) {
      return (
        <Typography color='textSecondary' variant='body2'>
          {t(general_messages.loading)}
        </Typography>
      );
    }
    if (sortedComments.length === 0) {
      return (
        <Typography color='textSecondary' variant='body2'>
          {t(action_library_messages.instance_actions.instance_comments_empty)}
        </Typography>
      );
    }
    return (
      <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell sortDirection={dateSortOrder}>
              <TableSortLabel active direction={dateSortOrder} onClick={handleDateSortClick}>
                {t(action_library_messages.instance.detail.action_log_date)}
              </TableSortLabel>
            </TableCell>
            <TableCell>{t(action_library_messages.instance.detail.action_log_user)}</TableCell>
            <TableCell>{t(action_library_messages.instance_actions.comments_column_comment)}</TableCell>
            <TableCell>{t(action_library_messages.instance_actions.comment_purpose_column)}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedComments.map((c: InstanceComment) => {
            const displayText = c.textPreview !== undefined && c.textPreview !== '' ? c.textPreview : c.text;
            const showTooltip = Boolean(c.isTruncated && c.text && c.text !== displayText);
            const purposeLabel = c.purpose != null ? t(getPurposeMessageKey(c.purpose)) : t(getPurposeMessageKey(1));
            const isPurposeSpecial = c.purpose === PURPOSE_DELEGATION || c.purpose === PURPOSE_COMPLETION;
            return (
              <TableRow key={c.id}>
                <TableCell>{formatDate(c.createdAt)}</TableCell>
                <TableCell>{c.userFullName || c.userEmail || '—'}</TableCell>
                <TableCell>
                  {showTooltip ? (
                    <Tooltip title={c.text}>
                      <Typography component='span' style={{ cursor: 'help' }} variant='body2'>
                        {displayText}
                      </Typography>
                    </Tooltip>
                  ) : (
                    <Typography variant='body2'>{displayText}</Typography>
                  )}
                </TableCell>
                <TableCell>
                  {isPurposeSpecial ? <Chip color='primary' label={purposeLabel} size='small' variant='outlined' /> : purposeLabel}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  };

  return (
    <Dialog fullWidth maxWidth='sm' onClose={onClose} open={open}>
      <DialogTitle>{t(action_library_messages.instance_actions.instance_comments_dialog_title)}</DialogTitle>
      <DialogContent dividers>
        <div style={{ maxHeight: 320, overflowY: 'auto', marginBottom: readOnly ? 0 : 16 }}>{renderCommentsContent()}</div>
        {!readOnly && (
          <form onSubmit={handleSubmit}>
            <TextField
              disabled={createCommentMutation.isLoading}
              fullWidth
              label={t(action_library_messages.instance_actions.instance_comments_add_placeholder)}
              multiline
              onChange={e => setNewCommentText(e.target.value)}
              placeholder={t(action_library_messages.instance_actions.instance_comments_add_placeholder)}
              rows={2}
              value={newCommentText}
              variant='outlined'
            />
          </form>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t(general_messages.close)}</Button>
        {!readOnly && (
          <Button
            color='primary'
            disabled={!newCommentText.trim() || createCommentMutation.isLoading}
            onClick={handleSubmit}
            variant='contained'
          >
            {t(general_messages.add)}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default InstanceCommentsDialog;
